import { useMatchMobile, useStaticData } from '@avensia/excite';
import { styled } from '@glitz/react';
import * as React from 'react';
import Image from 'webapp/components/image';
import Link from 'webapp/components/Link';
import { Block } from 'webapp/components/page-layout';
import { createMinLargeViewportDecorator, createMinMediumViewportDecorator } from 'webapp/viewport';
import { SiteLogo } from 'webapp/shared/logos';
import { LinkCollection } from 'webapp/components/LinkCollection';

export default function LinkFooter() {
  const isMobile = useMatchMobile();
  const linkFooter = useStaticData<NordicNest.Api.FooterApiModel>();
  const linkCollections = linkFooter?.linkCollections || [];

  const icons = (
    <Icons key="icons">
      {linkFooter?.socialLinks?.map((sl, i) => (
        <IconLink key={i} {...sl.link}>
          <Icon width={undefined} height={undefined} image={{ url: sl.icon } as Excite.ContentApi.ImageApiModel} />
        </IconLink>
      ))}
    </Icons>
  );

  const links = (
    <LinkCollections key="links">
      {linkCollections.map((linkCollection, i: number) => (
        <LinkCollection {...linkCollection} key={i} />
      ))}
      {linkFooter && linkFooter?.logos?.length > 0 && (
        <LogoList>
          {linkFooter.logos.map((logo, i) => (
            <LogoImage key={i} image={logo} />
          ))}
        </LogoList>
      )}
    </LinkCollections>
  );

  return (
    <Base>
      <Container>
        <TopIcon key="logo">
          <SiteLogo />
        </TopIcon>
        <CenteredText key="slogan">{linkFooter?.text}</CenteredText>
        {isMobile ? (
          <>
            {icons}
            {links}
          </>
        ) : (
          <>
            {links}
            {icons}
          </>
        )}
      </Container>
    </Base>
  );
}

const Icon = styled(Image, createMinMediumViewportDecorator({ margin: { x: t => t.spacing.s } }), {
  margin: { x: t => t.spacing.xs },
  height: 42,
  width: 42,
});

const CenteredText = styled.div({
  padding: { top: t => t.spacing.m },
  flex: {
    grow: 1,
  },
  textAlign: 'center',
  fontWeight: t => t.fontWeight?.bold,
  textTransform: 'uppercase',
  fontFamily: t => t.fontFamily?.secondary,
});

const LinkCollections = styled.div(
  createMinMediumViewportDecorator({
    margin: { x: t => t.spacing.l, top: t => t.spacing.xl },
    flexDirection: 'row',
    gap: t => t.spacing.xxl,
  }),
  {
    flexDirection: 'column',
    display: 'flex',
    justifyContent: 'center',
    flex: {
      grow: 1,
    },
    flexWrap: 'wrap',
  },
);

const TopIcon = styled.div({
  display: 'flex',
  justifyContent: 'center',
  flex: {
    grow: 1,
  },
});

const Icons = styled.div(
  createMinMediumViewportDecorator({
    padding: { top: t => t.spacing.xl, bottom: t => t.spacing.xxl },
  }),
  {
    display: 'flex',

    justifyContent: 'center',
    flex: {
      grow: 1,
    },
    padding: { bottom: t => t.spacing.xl, top: t => t.spacing.m },
  },
);

const LogoList = styled.div(
  createMinMediumViewportDecorator({
    gap: t => t.sharedSpacing.l,
    flexDirection: 'column',
    border: { top: { width: 0 } },
    justifyContent: 'flex-start',
    padding: { xy: 0 },
  }),
  {
    display: 'flex',
    alignItems: 'center',
    border: { top: { color: t => t.monochrome.color, style: 'solid', width: 'thin' } },
    justifyContent: 'center',
    gap: t => t.sharedSpacing.m,
    padding: { xy: t => t.sharedSpacing.l },
  },
);

const LogoImage = styled(Image, {
  maxHeight: 150,
  maxWidth: 150,
  objectFit: 'contain',
});

const IconLink = styled(Link, {
  fontSize: t => t.fontSize.xl,
  ':hover': {
    backgroundColor: 'inherit',
  },
});

const Base = styled.div(
  createMinMediumViewportDecorator({ paddingTop: t => t.spacing.xxl, backgroundColor: t => t.footer?.color }),
  {
    padding: { top: t => t.spacing.xl },
    backgroundColor: t => t.footer?.accordion?.backgroundColor,
  },
);

const Container = styled(Block, createMinLargeViewportDecorator({ minHeight: 100 }), {
  display: 'flex',
  flexDirection: 'column',
});
